import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/site/ui";
import { Footer } from "@/components/site/Footer";

const LegalLayout = ({ title, updated, testid, children }) => {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="bg-white min-h-screen antialiased">
      <header className="border-b border-navy-100 bg-white/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" aria-label="Konek Church home"><Logo /></Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-gold-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 rounded-md px-2 py-1"
          >
            <ArrowLeft size={16} aria-hidden /> Back to home
          </Link>
        </div>
      </header>
      <main id="main" data-testid={testid} className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-navy-900">{title}</h1>
        <p className="mt-2 text-sm text-charcoal-700/60">Last updated: {updated}</p>
        <div className="mt-10 space-y-8 text-charcoal-700 leading-relaxed [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-navy-900 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_a]:text-navy-900 [&_a]:underline [&_a]:underline-offset-2">
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const UPDATED = "June 2026";

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" updated={UPDATED} testid="privacy-page">
    <section>
      <h2>Who we are</h2>
      <p>
        Konek Church ("we", "us") is a digital-giving and technology-solutions brand operated by
        Konekte Haitians LLC, serving churches in South Florida. This policy explains what
        information we collect through konekchurch.com and how we use it.
      </p>
    </section>
    <section>
      <h2>Information we collect</h2>
      <ul>
        <li><strong>Consultation form:</strong> your name, church name, email, phone, church location, preferred language, area of interest, and any message you choose to share.</li>
        <li><strong>Public-display preference:</strong> whether you allow your church's name to appear in our recent-activity notifications (optional and off by default).</li>
      </ul>
      <p>We do not collect payment card information on this website.</p>
    </section>
    <section>
      <h2>How we use your information</h2>
      <ul>
        <li>To respond to your consultation request and contact you about the services you asked about.</li>
        <li>To send a one-time confirmation email when you submit a request.</li>
        <li>If you opt in, to display your church's name in our recent-activity notifications after our team approves it.</li>
      </ul>
      <p>We do not sell, rent, or trade your personal information. We do not send marketing email without your consent.</p>
    </section>
    <section>
      <h2>How we store and share it</h2>
      <p>
        Form submissions are stored securely in our database and delivered to our team by an email
        service provider acting on our behalf. We share information only with service providers
        needed to operate this website and, where you pursue payment-processing services, with the
        third-party provider you choose to apply with.
      </p>
    </section>
    <section>
      <h2>Your choices</h2>
      <ul>
        <li>You may request access to, correction of, or deletion of your information at any time.</li>
        <li>You may withdraw permission for your church's name to appear in activity notifications at any time.</li>
      </ul>
      <p>
        Contact us at <a href="mailto:hello@konekchurch.com">hello@konekchurch.com</a> for any
        privacy request and we will respond promptly.
      </p>
    </section>
    <section>
      <h2>Children</h2>
      <p>This website is intended for church leaders and adults. We do not knowingly collect information from children under 13.</p>
    </section>
    <section>
      <h2>Changes</h2>
      <p>If we update this policy, we will post the new version on this page with a revised date.</p>
    </section>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout title="Terms and Conditions" updated={UPDATED} testid="terms-page">
    <section>
      <h2>About our services</h2>
      <p>
        Konek Church, operated by Konekte Haitians LLC, provides church website design, online-giving
        setup assistance, digital-communication tools, and related consulting and support services.
        By using this website or engaging our services, you agree to these terms.
      </p>
    </section>
    <section>
      <h2>Consultations and quotes</h2>
      <p>
        Consultations are free and carry no obligation. Package prices shown on this website are
        starting prices; a final quote is confirmed in writing after we understand your church's
        needs. Work begins after a written agreement and any agreed deposit.
      </p>
    </section>
    <section>
      <h2>Third-party payment processing</h2>
      <p>
        Konek Church is <strong>not</strong> a payment processor, bank, or money-services business.
        Merchant approval, equipment, processing rates, transaction fees, deposits, chargebacks, and
        device-related technical support are determined and handled by participating third-party
        payment-processing providers under their own terms. See our{" "}
        <a href="/payment-disclosure">Payment-Processing Disclosure</a>.
      </p>
    </section>
    <section>
      <h2>Website ownership</h2>
      <p>
        Websites and content we build for your church belong to your church upon full payment.
        Domains, hosting, and third-party software carry their own separate terms and recurring
        costs, which remain your responsibility unless a care plan states otherwise.
      </p>
    </section>
    <section>
      <h2>Monthly care plans</h2>
      <p>
        Care plans include only the items, update allowances, support methods, and response times
        listed for each plan. Plans are not unlimited. Larger projects, redesigns, and new
        integrations are quoted separately. Plans may be cancelled with written notice before the
        next billing cycle.
      </p>
    </section>
    <section>
      <h2>No guarantees</h2>
      <p>
        We work diligently and honestly, but we do not guarantee specific giving amounts, growth
        results, search rankings, or approval by any third-party provider.
      </p>
    </section>
    <section>
      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, Konekte Haitians LLC's total liability arising from
        our services is limited to the amounts you paid us for the service giving rise to the claim.
        We are not liable for the acts, omissions, fees, or decisions of third-party providers.
      </p>
    </section>
    <section>
      <h2>Governing law</h2>
      <p>These terms are governed by the laws of the State of Florida, USA.</p>
    </section>
    <section>
      <h2>Contact</h2>
      <p>Questions about these terms: <a href="mailto:hello@konekchurch.com">hello@konekchurch.com</a>.</p>
    </section>
  </LegalLayout>
);

export const PaymentDisclosure = () => (
  <LegalLayout title="Payment-Processing Disclosure" updated={UPDATED} testid="payment-disclosure-page">
    <section>
      <p>
        This disclosure explains, in plain language, the role Konek Church (operated by Konekte
        Haitians LLC) plays when your church adopts a giving device or online giving.
      </p>
    </section>
    <section>
      <h2>Konek Church is not a payment processor</h2>
      <p>
        We are a technology-solutions and consulting company. We are not a payment processor, bank,
        card network, or money-services business, and we never hold, transmit, or have access to
        your church's donation funds.
      </p>
    </section>
    <section>
      <h2>What the third-party provider decides</h2>
      <ul>
        <li>Merchant-account approval or denial, and all contractual terms.</li>
        <li>Processing rates, transaction fees, and any equipment costs.</li>
        <li>Deposit timing, holds, and chargeback handling.</li>
        <li>Device-related technical support and transaction disputes.</li>
      </ul>
    </section>
    <section>
      <h2>What Konek Church does</h2>
      <ul>
        <li>Helps your church understand its options and complete provider applications accurately.</li>
        <li>Coordinates the setup of approved devices and online-giving connections with your website.</li>
        <li>Trains your team and provides ongoing website and digital support under your chosen plan.</li>
      </ul>
    </section>
    <section>
      <h2>No compensation-based recommendations</h2>
      <p>
        Our recommendations are based on your church's needs and budget. Where any referral
        arrangement with a provider exists, it does not change the rates the provider offers you,
        and we will disclose it during your consultation upon request.
      </p>
    </section>
    <section>
      <h2>Questions</h2>
      <p>
        We are glad to walk through any provider's terms with you before you sign. Write to{" "}
        <a href="mailto:hello@konekchurch.com">hello@konekchurch.com</a>.
      </p>
    </section>
  </LegalLayout>
);
